// @ts-nocheck
import React, { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getCurrentUserGroups } from '../../../utils/ownerScope';
import { ReactComponent as CloseIcon } from '../../../assets/icons/x-close.svg';
import { setModalState, modalViewsConst } from '../../../store/modalSlice';
import { fetchModelSchemaTemplates } from '../../../store/modelSchemaTemplateSlice';
import ModelCard, { ModelCardSkeleton } from './ModelCard';
import SearchFilter from './SearchFilter';
import { ModelGardenItem } from './types';
import { generateClient } from 'aws-amplify/api';
import {
  getUserForCard,
  getPublicProfileForCard,
  getOrgPublicProfileForCard,
} from '../../../graphql/customQueries';
import { SkeletonGroup } from '../../Skeleton';

const client = generateClient();

interface ModelGardenModalProps {
  onClose: () => void;
}

const ModelGardenModal: React.FC<ModelGardenModalProps> = ({ onClose }) => {
  const dispatch = useDispatch();
  const modelSchemaTemplateState = useSelector((state: any) => state.modelSchemaTemplates);
  const [searchValue, setSearchValue] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState('recommended');
  const [userCache, setUserCache] = useState<Record<string, any>>({});
  // Cache of resolved author public profiles, keyed by authorProfileId. Intentionally per-mount and not invalidated — author name/avatar refreshes the next time the modal opens.
  const [authorProfileCache, setAuthorProfileCache] = useState<Record<string, any>>({});
  // Null until the session is read; non-admins see historical templates only
  const [cognitoGroups, setCognitoGroups] = useState<string[] | null>(null);
  const isAdmin = Boolean(cognitoGroups?.includes("Admin") || cognitoGroups?.includes("AdminReadOnly"));
  useEffect(() => {
    getCurrentUserGroups().then(setCognitoGroups);
  }, []);

  // Fetch templates on mount
  useEffect(() => {
    dispatch(fetchModelSchemaTemplates());
  }, [dispatch]);

  // Fetch user data for creators
  useEffect(() => {
    const fetchCreatorData = async () => {
      const templateList = modelSchemaTemplateState.templateList || [];
      const uniqueCreatorIds = [...new Set(templateList
        .filter((t: any) => t.createdBy)
        .map((t: any) => t.createdBy))];
      
      const idsToFetch = uniqueCreatorIds.filter((id: string) => !userCache[id]);

      if (idsToFetch.length === 0) return;

      // Fetch all user data concurrently
      const results = await Promise.all(
        idsToFetch.map((userId: string) =>
          client.graphql({
            query: getUserForCard,
            variables: { id: userId }
          })
            .then(({ data }) => ({ userId, user: data?.getUser }))
            .catch((error) => {
              console.error(`Error fetching user ${userId}:`, error);
              return { userId, user: null };
            })
        )
      );

      const newUserCache: Record<string, any> = {};
      results.forEach(({ userId, user }) => {
        if (user) {
          newUserCache[userId] = user;
        }
      });

      if (Object.keys(newUserCache).length > 0) {
        setUserCache(prev => ({ ...prev, ...newUserCache }));
      }
    };

    if (modelSchemaTemplateState.templateList?.length > 0) {
      fetchCreatorData();
    }
  }, [modelSchemaTemplateState.templateList]);

  // Fetch the credited author's public profile (USER -> PublicProfile, ORGANIZATION -> OrganizationPublicProfile), keyed by authorProfileId.
  useEffect(() => {
    const fetchAuthorProfiles = async () => {
      const templateList = modelSchemaTemplateState.templateList || [];
      const authors = templateList.filter((t: any) => t.authorType && t.authorProfileId);
      // De-dupe by authorProfileId, skipping ones already cached.
      const toFetch = Array.from(
        new Map(
          authors
            .filter((t: any) => !(t.authorProfileId in authorProfileCache))
            .map((t: any) => [t.authorProfileId, t])
        ).values()
      );

      if (toFetch.length === 0) return;

      const results = await Promise.all(
        toFetch.map((t: any) =>
          client
            .graphql({
              query: t.authorType === 'ORGANIZATION' ? getOrgPublicProfileForCard : getPublicProfileForCard,
              variables: { id: t.authorProfileId },
            })
            .then(({ data }: any) => ({
              id: t.authorProfileId,
              profile: data?.getPublicProfile || data?.getOrganizationPublicProfile || null,
            }))
            .catch((error) => {
              console.error(`Error fetching author profile ${t.authorProfileId}:`, error);
              return { id: t.authorProfileId, profile: null };
            })
        )
      );

      // Store every fetched id (even nulls) so we don't re-fetch missing profiles every render.
      setAuthorProfileCache((prev) => {
        const next = { ...prev };
        results.forEach(({ id, profile }) => {
          next[id] = profile;
        });
        return next;
      });
    };

    if (modelSchemaTemplateState.templateList?.length > 0) {
      fetchAuthorProfiles();
    }
  }, [modelSchemaTemplateState.templateList]);

  // Resolve the credited author, honoring the public profile's visibility toggles. Fallback chain: chosen public profile -> legacy createdBy -> User lookup -> "Erode AI".
  const resolveCreator = (template: any) => {
    const publicProfile = template.authorProfileId ? authorProfileCache[template.authorProfileId] : null;

    if (publicProfile) {
      return {
        name: (publicProfile.showDisplayName && publicProfile.displayName) || 'Erode AI',
        avatar: undefined,
        profilePictureUrl:
          (publicProfile.showProfilePicture && publicProfile.profilePictureUrl) || undefined,
      };
    }

    // No author profile: resolve the creator's User record (same-org only) then fall back.
    const creatorUser = template.createdBy ? userCache[template.createdBy] : null;
    return {
      name: creatorUser?.name || 'Erode AI',
      avatar: undefined,
      profilePictureUrl: creatorUser?.profilePictureUrl || undefined,
    };
  };

  // Transform templates to ModelGardenItem format
  const transformTemplateToModelItem = (template: any): ModelGardenItem => {
    return {
      id: template.id,
      name: template.name,
      description: template.description,
      type: template.createdBy ? 'Internal' : 'Community',
      tags: template.tags || [],
      category: template.category,
      version: template.version,
      thumbnail: template.profilePictureUrl || undefined,
      executionUrl: template.executionUrl,
      modelKind: template.modelKind ?? null,
      external: template.external === true,
      creator: resolveCreator(template),
      templateData: template
    };
  };

  // Get templates from Redux store and transform them
  const modelTemplates = useMemo(() => {
    const templateList = modelSchemaTemplateState.templateList || [];
    return templateList
      // Strict check: only explicitly-active templates are selectable — null/missing isActive counts as inactive.
      .filter((template: any) => template.isActive === true)
      // Forecast creation stays admin-only (stage 4 of the historical models, decision 1). Until the groups are
      // known nothing is listed, so an Admin doesn't see the list flash from historical-only to everything
      // External forecast templates (no processor; forecasts are uploaded) are open to everyone too
      .filter((template: any) => cognitoGroups !== null && (isAdmin || template.modelKind === "HISTORICAL" || template.external === true))
      .map(transformTemplateToModelItem);
  }, [modelSchemaTemplateState.templateList, userCache, authorProfileCache, isAdmin, cognitoGroups]);

  // Extract available tags from templates
  const availableTags = useMemo(() => {
    const tagSet = new Set<string>();
    modelTemplates.forEach(model => {
      // For now, just use categories as tags until backend supports tags
      if (model.category) tagSet.add(model.category);
    });
    return Array.from(tagSet).sort();
  }, [modelTemplates]);

  const handleTagToggle = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const handleModelSelect = (model: ModelGardenItem) => {
    // Close current modal and open the new processed model schema modal with the selected template data
    onClose();
    
    setTimeout(() => {
      dispatch(
        setModalState({
          modalOpen: true,
          modalView: modalViewsConst.newProcessedModelSchema,
          properties: {
            __modalSize: "md",
            selectedTemplate: (model as any).templateData || model
          }
        })
      );
    }, 100);
  };


  // Filter and sort models
  const filteredModels = useMemo(() => {
    let models = [...modelTemplates];
    
    // Filter by search
    if (searchValue) {
      models = models.filter(model => 
        model.name.toLowerCase().includes(searchValue.toLowerCase()) ||
        model.description.toLowerCase().includes(searchValue.toLowerCase())
      );
    }
    
    // Filter by tags (using category for now)
    if (selectedTags.length > 0) {
      models = models.filter(model => 
        selectedTags.includes(model.category)
      );
    }
    
    // Sort
    switch(sortBy) {
      case 'popular':
        // Mock sorting - in real app this would be based on usage stats
        models.reverse();
        break;
      case 'recent':
        // Mock sorting - in real app this would be based on creation date
        models.sort((a, b) => b.id.localeCompare(a.id));
        break;
      case 'myModels':
        // In future, this will show user's deployed ProcessedModelSchemas For now, show Internal templates
        models = models.filter(m => m.type === 'Internal');
        break;
      default:
        // Keep as is for 'recommended'
        break;
    }
    
    return models;
  }, [modelTemplates, searchValue, selectedTags, sortBy]);

  // Loading state (the session's groups count as loading too: they decide which templates show)
  if (modelSchemaTemplateState.loading === 'pending' || cognitoGroups === null) {
    return (
      <div className="flex flex-col h-full">
        <div className="px-6 py-4 border-b border-zinc-200">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="eai-h3-title">
                Energy Model Library
              </h2>
              <p className="eai-description-sm mt-1">
                Loading model templates...
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-zinc-900 transition-colors"
            >
              <CloseIcon className="h-6 w-6" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-hidden p-6">
          <SkeletonGroup
            count={8}
            label="Loading model templates"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
          >
            <ModelCardSkeleton />
          </SkeletonGroup>
        </div>
      </div>
    );
  }

  // Error state
  if (modelSchemaTemplateState.error) {
    return (
      <div className="flex flex-col h-full">
        <div className="px-6 py-4 border-b border-zinc-200">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="eai-h3-title">
                Energy Model Library
              </h2>
              <p className="mt-1 text-sm text-red-500">
                Error loading model templates
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-zinc-900 transition-colors"
            >
              <CloseIcon className="h-6 w-6" />
            </button>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="eai-description-sm mb-4">Failed to load model templates</p>
            <button
              onClick={() => dispatch(fetchModelSchemaTemplates())}
              className="eai-btn-cta"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full max-h-[85dvh]">
      {/* Header */}
      <div className="px-6 py-4 border-b border-zinc-200">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="eai-h3-title">
              Energy Model Library
            </h2>
            <p className="eai-description-sm mt-1">
              Choose from community models or create your own forecasting model
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-zinc-900 transition-colors"
          >
            <CloseIcon className="h-6 w-6" />
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="px-6 py-4">
        <SearchFilter
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          selectedTags={selectedTags}
          onTagToggle={handleTagToggle}
          availableTags={availableTags}
          sortBy={sortBy}
          onSortChange={setSortBy}
        />
      </div>

      {/* Model Grid */}
      <div className="flex-1 overflow-y-auto px-6 pb-6">
        {filteredModels.length === 0 ? (
          <div className="text-center py-12">
            <p className="eai-description-sm">No models found matching your criteria</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredModels.map(model => (
              <ModelCard
                key={model.id}
                model={model}
                onClick={handleModelSelect}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ModelGardenModal;